<template>
  <div>
    <md-app-toolbar>
      <div class="md-toolbar-row">
        <ScenarioHeader
          :scenario-id="parseInt($route.params.scenarioId)"
          :scenario-state="scenarioData.scenario_state"/>
      </div>
    </md-app-toolbar>

    <md-content class="script__wrapper">
      <div
        class="alert alert-danger scenario-update-modal"
        v-if='scenarioResultsValids'>
        <div class="md-title">Modification du scénario</div>
        <div class="alert__content">
          <p class="md-body-2">Attention, toute modification dans cet écran va écraser les résultats actuels et annuler les modifications saisies dans
            <router-link
              class="md-accent-link"
              :to="{ name: 'Scenario refine', params: { scenarioId: $route.params.scenarioId }}"><strong>"Affinage"</strong></router-link>.
          </p>
          <p class="md-body-2">Afin de conserver vos modifications, vous pouvez dupliquer le scénario actuel depuis la section
            <router-link
              class="md-accent-link"
              :to="{ name: 'Scenarios'}"><strong>"Scénarios"</strong></router-link>.
          </p>
        </div>
      </div>
      <div
        class="alert alert-danger scenario-errors-modal mb-20"
        v-if='scenarioData.generation_failed'>
        <div class="md-title">Erreur de génération</div>
        <div class="alert__content">
          <p class="md-body-2">Une erreur s'est produite lors de la dernière génération du scénario : <a
            @click.prevent="showSimulationError"
            href="#"><strong>consulter les détails de l'erreur</strong></a>.
          </p>
        </div>
      </div>
      <div class="top-actions clearfix">
        <div class="float-right">
          <md-button
            @click="showConfirmUpdateModal(generateScenario, null)"
            class="md-raised md-primary">
            Calculer
          </md-button>
        </div>
      </div>

      <h2>Votre script</h2>
      <div class="box">
        <md-table
          md-card
          v-model="scenarioActions"
          key="included-actions-table">
          <md-table-toolbar>
            <h1 class="md-title">Actions incluses</h1>
          </md-table-toolbar>
          <md-table-empty-state
            md-label="Aucune action définie"
            md-description="Votre simulation ne contient aucune action, vous devez en définir au moins une pour pouvoir démarrer la simulation." />
          <md-table-row
            slot="md-table-row"
            slot-scope="{ item }">
            <md-table-cell
              md-label="Nom">
              {{ item.name }}
            </md-table-cell>
            <md-table-cell
              md-label="Action">
              <strong>{{ item.action_label }}</strong>
              <div><em>{{ item.action_sub_category_label }}</em></div>
            </md-table-cell>
            <md-table-cell
              md-label="Appliqué à">
              <strong v-if='item.function || item.place_type'>{{ item.function_label }} <span v-if='item.function && item.place_type'> - </span>{{ item.place_type_display }}</strong>
              <strong v-if='item.places'>{{ item.places_display }}</strong>
              <div v-if='item.owner_applying || item.co_owner_applying || item.tenant_applying || item.part_owner_applying'><em>{{ item|ownerStatus }}</em> <sup>*</sup><md-tooltip md-direction="top"><em>PO : Propriétaire occupant</em> / <em>PB : Propriétaire bailleur</em> / <em>L : Locataire</em> / <em>C : Copropriétaire</em></md-tooltip></div>
            </md-table-cell>
            <md-table-cell>
              <i class="material-icons">
                info
              </i>
            </md-table-cell>
            <md-table-cell
              md-label="Départ">
              {{ item.start_year }}
            </md-table-cell>
            <md-table-cell
              md-label="Nb demandé / an">
              <YearlyValuesField
                unit=" action(s)"
                field-title="Rythme annuel"
                counter-title="Nombre total"
                :show-counter="true"
                :years-cluser-list="scenarioYearsCluserList"
                :initial-values-list="item.rhythms"
                :min-year="scenarioYear"
                :max-year="scenarioMaxYear"
                :read-only="true" />
            </md-table-cell>
            <md-table-cell
              md-label="Total demandé">
              {{ item.rhythms_total }}
            </md-table-cell>
            <md-table-cell
              md-label="Lieux concernés">
              <div>
                <strong>{{ item.concerned_places_number_display }}</strong>
              </div>
              <div>
                <em>{{ item.concerned_places_area|toInteger }} m<sup>2</sup></em>
              </div>
            </md-table-cell>
            <md-table-cell>
              <md-button
                @click="showUpdateIncludedActionModal(item)"
                class="md-primary">
                Modifier
              </md-button>
            </md-table-cell>
            <md-table-cell>
              <md-button
                @click="showConfirmUpdateModal(deleteScenarioAction, item.id)"
                class="md-primary">
                Supprimer
              </md-button>
            </md-table-cell>
          </md-table-row>
        </md-table>
      </div>

      <div class="box">
        <md-table
          md-card
          :md-sort.sync="excludedActionsCurrentSort"
          :md-sort-order.sync="excludedActionsCurrentSortOrder"
          :md-sort-fn="excludedActionsCustomSort"
          v-model="placeExcludedActions"
          key="excluded-actions-table">
          <md-table-toolbar>
            <h1 class="md-title">Actions exclues</h1>
          </md-table-toolbar>
          <md-table-empty-state
            md-label="Aucune action exclue"
            md-description="Utilisez le formulaire ci-après pour exclure des actions de votre script" />
          <md-table-row
            slot="md-table-row"
            slot-scope="{ item }">
            <md-table-cell
              md-label="Nom">
              {{ item.name }}
            </md-table-cell>
            <md-table-cell
              md-sort-by="action_label"
              md-label="Action">
              <strong>{{ item.action_label }}</strong>
              <div><em>{{ item.action_sub_category_label }}</em></div>
            </md-table-cell>
            <md-table-cell
              md-sort-by="place_details.identifier"
              md-label="Lieu">
              <strong>
                <div>
                  {{ item.place_details.name }} ({{ item.place_details.identifier }})
                </div>
              </strong>
            </md-table-cell>
            <md-table-cell
              md-label="Commentaire">
              {{ item.comment }}
            </md-table-cell>
            <md-table-cell>
              <md-button
                @click="showConfirmUpdateModal(deletePlaceExcludedAction, item.id)"
                class="md-primary">
                Supprimer
              </md-button>
            </md-table-cell>
          </md-table-row>
        </md-table>
      </div>

      <div class="box box-form">
        <h3>Inclure une nouvelle action au script</h3>
        <div>
          <md-radio
            v-model="userActionType"
            value="global-action"
            class="md-primary">
            Ajouter une action générale
          </md-radio>
          <md-radio
            v-model="userActionType"
            value="place-action"
            class="md-primary">
            Ajouter une action sur une liste de lieux
          </md-radio>
          <md-radio
            v-model="userActionType"
            value="exclude-action"
            class="md-primary">
            Exclure une action sur un lieu
          </md-radio>
        </div>

        <div
          v-if='userActionType === "global-action" || userActionType === "place-action"'>
          <div class="md-layout md-gutter">
            <div class="md-layout-item">
              <md-field :class="{ 'md-has-value': true, 'md-invalid': scenarioActionFieldsErrors.name }">
                <label for="name">Nom</label>
                <md-input
                  placeholder="Nom"
                  v-model="newScenarioAction.name"
                  required />
                <span
                  class="md-error"
                  v-if="scenarioActionFieldsErrors.name">{{ scenarioActionFieldsErrors.name }}</span>
              </md-field>
            </div>
            <div class="md-layout-item">
              <md-field :class="{ 'md-has-value': true }">
                <label for="action_category">Catégorie d'action</label>
                <md-select
                  md-dense
                  md-class="md-menu-content-very-huge"
                  v-model="newScenarioAction.selectedActionCategory"
                  name="action_category"
                  id="action_category"
                  placeholder="Choisissez une catégorie d'action">
                  <md-optgroup
                    v-for="actionsCategory in actionsCategories"
                    :label="actionsCategory.label"
                    :key="actionsCategory.slug">
                    <md-option
                      :key="actionsSubCategory.slug"
                      v-for="actionsSubCategory in actionsCategory.sub_categories"
                      :value="actionsSubCategory.id">
                      {{ actionsSubCategory.label }}
                    </md-option>
                  </md-optgroup>
                </md-select>
              </md-field>
            </div>
            <!-- https://github.com/vuematerial/vue-material/issues/1681 -->
            <div class="md-layout-item">
              <md-field
                :class="{ 'md-has-value': true, 'md-invalid': scenarioActionFieldsErrors.selectedAction }">
                <label for="action">Action</label>
                <md-select
                  md-dense
                  md-class="md-menu-content-very-huge"
                  v-model="newScenarioAction.selectedAction"
                  name="action"
                  id="action"
                  placeholder="Choisissez une action"
                  required>
                  <md-option
                    v-for="action in actionList"
                    :key="action.id"
                    :value="action.id">
                    {{ action.label }}
                  </md-option>
                </md-select>
                <span
                  class="md-error"
                  v-if="scenarioActionFieldsErrors.selectedAction">{{ scenarioActionFieldsErrors.selectedAction }}</span>
              </md-field>
            </div>
            <div
              class="md-layout-item"
              v-if='userActionType === "global-action"'>
              <md-field
                :class="{ 'md-has-value': true, 'md-invalid': scenarioActionFieldsErrors.selectedFunction }">
                <label for="action_function">Fonction</label>
                <md-select
                  md-dense
                  v-model="newScenarioAction.selectedFunction"
                  name="function"
                  id="function"
                  placeholder="Choisissez une fonction">
                  <md-option
                    v-for="func in functionList"
                    :key="func.slug"
                    :value="func.id">
                    {{ func.label }}
                  </md-option>
                </md-select>
                <span
                  class="md-error"
                  v-if="scenarioActionFieldsErrors.selectedFunction">{{ scenarioActionFieldsErrors.selectedFunction }}</span>
              </md-field>
            </div>
            <div
              class="md-layout-item"
              v-if='userActionType === "global-action"'>
              <md-field
                :class="{ 'md-has-value': true, 'md-invalid': scenarioActionFieldsErrors.selectedPlaceType }">
                <label for="place_type">Type de lieux</label>
                <md-select
                  md-dense
                  v-model="newScenarioAction.selectedPlaceType"
                  name="place_type"
                  id="place_type"
                  placeholder="Choisissez un type de lieu">
                  <md-option
                    v-for="(placeType, i) in placeTypeList"
                    :key="i"
                    :value="placeType.value">
                    {{ placeType.label }}
                  </md-option>
                </md-select>
                <span
                  class="md-error"
                  v-if="scenarioActionFieldsErrors.selectedPlaceType">{{ scenarioActionFieldsErrors.selectedPlaceType }}</span>
              </md-field>
            </div>
          </div>
          <div class="md-layout md-gutter">
            <div
              class="md-layout-item"
              v-if='userActionType === "place-action"'>
              <places-input
                v-if="newScenarioAction.selectedAction"
                v-model="newScenarioAction.selectedPlaces"
                :required="true"
                :places-type="newActionplacesType"
                label="Lieux concernés"
              />
              <md-field
                v-else
                :class="{ 'md-has-value': true}">
                <label for="name">Lieux concernés</label>
                <md-input
                  placeholder="Vous devez saisir une action avant de pouvoir sélectionner les lieux concernés"
                  disabled
                  required />
              </md-field>
            </div>
          </div>
          <div class="md-layout md-gutter">
            <div class="md-layout-item md-size-25">
              <md-field
                :class="{ 'md-has-value': true, 'md-invalid': scenarioActionFieldsErrors.selectedYear }">
                <label for="scenarioYear">Départ</label>
                <md-select
                  v-model="newScenarioAction.selectedYear"
                  name="year"
                  placeholder="Choisissez une année de départ"
                  required>
                  <md-option
                    v-for="scenarioYear in scenarioYears"
                    :key='scenarioYear'
                    :value="scenarioYear">{{ scenarioYear }}</md-option>
                </md-select>
                <span
                  class="md-error"
                  v-if="scenarioActionFieldsErrors.selectedYear">{{ scenarioActionFieldsErrors.selectedYear }}</span>
              </md-field>
            </div>
            <div class="md-layout-item md-size-25">
              <YearlyValuesField
                unit=" action(s)"
                field-title="Rythme annuel"
                counter-title="Nombre total"
                :show-counter="true"
                :years-cluser-list="scenarioYearsCluserList"
                :initial-values-list="newScenarioAction.rhythms"
                :min-year="scenarioYear"
                :max-year="scenarioMaxYear"
                @validate:valuesList="newScenarioAction.rhythms = $event" />
            </div>
          </div>
          <div class="md-layout md-gutter">
            <div class="md-layout-item">
              <div class="md-caption">Appliquer si</div>
              <div>
                <md-checkbox
                  class="md-primary"
                  v-model="newScenarioAction.ownerApplying">
                  Propriétaire occupant
                </md-checkbox>
                <md-checkbox
                  class="md-primary"
                  v-model="newScenarioAction.coOwnerApplying">
                  Propriétaire bailleur
                </md-checkbox>
                <md-checkbox
                  class="md-primary"
                  v-model="newScenarioAction.tenantApplying">
                  Locataire
                </md-checkbox>
                <md-checkbox
                  class="md-primary"
                  v-model="newScenarioAction.partOwnerApplying">
                  Copropriétaire
                </md-checkbox>
              </div>
            </div>
            <div class="md-layout-item">
              <md-button
                @click="showConfirmUpdateModal(createScenarioAction, newScenarioAction)"
                class="md-raised md-primary float-right">
                Ajouter l'action
              </md-button>
            </div>
          </div>
        </div>
        <div
          v-else>
          <div class="md-layout md-gutter">
            <div class="md-layout-item">
              <md-field :class="{ 'md-has-value': true, 'md-invalid': placeExcludedActionFieldsErrors.name }">
                <label for="name">Nom</label>
                <md-input
                  placeholder="Nom"
                  v-model="newPlaceExcludedAction.name"
                  required />
                <span
                  class="md-error"
                  v-if="placeExcludedActionFieldsErrors.name">{{ placeExcludedActionFieldsErrors.name }}</span>
              </md-field>
            </div>

            <div class="md-layout-item">
              <md-field :class="{ 'md-has-value': true }">
                <label for="action_category">Catégorie d'action</label>
                <md-select
                  md-dense
                  md-class="md-menu-content-very-huge"
                  v-model="newPlaceExcludedAction.selectedActionCategory"
                  name="action_category"
                  id="action_category"
                  placeholder="Choisissez une catégorie d'action">
                  <md-optgroup
                    v-for="actionsCategory in actionsCategories"
                    :label="actionsCategory.label"
                    :key="actionsCategory.slug">
                    <md-option
                      :key="actionsSubCategory.slug"
                      v-for="actionsSubCategory in actionsCategory.sub_categories"
                      :value="actionsSubCategory.id">
                      {{ actionsSubCategory.label }}
                    </md-option>
                  </md-optgroup>
                </md-select>
              </md-field>
            </div>
            <!-- https://github.com/vuematerial/vue-material/issues/1681 -->
            <div class="md-layout-item">
              <md-field
                :class="{ 'md-has-value': true, 'md-invalid': placeExcludedActionFieldsErrors.selectedAction }">
                <label for="action">Action</label>
                <md-select
                  md-dense
                  md-class="md-menu-content-very-huge"
                  v-model="newPlaceExcludedAction.selectedAction"
                  name="action"
                  id="action"
                  placeholder="Choisissez une action"
                  required>
                  <md-option
                    v-for="action in actionList"
                    :key="action.id"
                    :value="action.id">
                    {{ action.label }}
                  </md-option>
                </md-select>
                <span
                  class="md-error"
                  v-if="placeExcludedActionFieldsErrors.selectedAction">{{ placeExcludedActionFieldsErrors.selectedAction }}</span>
              </md-field>
            </div>
          </div>
          <div class="md-layout md-gutter">
            <div class="md-layout-item">
              <md-field
                :class="{ 'md-has-value': true, 'md-required': true, 'md-invalid': placeExcludedActionFieldsErrors.place }">
                <label for="area">Lieu concerné</label>
                <multiselect
                  name="area"
                  v-model="newPlaceExcludedAction.selectedPlace"
                  :options="placeList"
                  :preserve-search="true"
                  :custom-label="placeChoiceName"
                  label="name"
                  track-by="id"
                  placeholder="Sélectionnez un lieu"
                  select-label="Sélectionner"
                  select-group-label="Sélectionner le groupe"
                  selected-label="Sélectionné ✓"
                  deselect-label="Désélectionner ✗"
                  deselect-group-label="Désélectionner le groupe ✗"
                  class="multiselect-transparent">
                  <span slot="noResult">Aucun lieu trouvé, veuillez modifier votre recherche.</span>
                </multiselect>
                <span
                  class="md-error"
                  v-if="placeExcludedActionFieldsErrors.place">{{ placeExcludedActionFieldsErrors.place }}</span>
              </md-field>
            </div>
          </div>
          <div class="md-layout md-gutter">
            <div /><!-- keep this to avoid a bug when the textarea is hidden -->
            <div class="md-layout-item">
              <md-field>
                <label for="comment">Commentaire</label>
                <md-textarea
                  name="comment"
                  v-model="newPlaceExcludedAction.comment"/>
              </md-field>
            </div>
          </div>
          <div class="md-layout md-gutter">
            <div class="md-layout-item">
              <md-button
                @click="showConfirmUpdateModal(createPlaceExcludedAction, newPlaceExcludedAction)"
                class="md-raised md-primary float-right">
                Exclure l'action
              </md-button>
            </div>
          </div>
        </div>
      </div>

      <div
        class="mt-40">
        <h2>Changements d'énergies</h2>
        <div class="box">
          <md-table
            md-card
            v-model="energySwitchPlaces">
            <md-table-toolbar>
              <h1 class="md-title">Changements d'énergies par lieux</h1>
            </md-table-toolbar>
            <md-table-empty-state
              md-label="Aucun changement d'énergie paramétré"
              md-description="Utilisez le formulaire ci-après pour paramétrer des changements d'énergie" />
            <md-table-row
              slot="md-table-row"
              slot-scope="{ item }">
              <md-table-cell
                md-label="Nom"
                width="30%">
                {{ item.name }}
              </md-table-cell>
              <md-table-cell
                md-label="Énergie"
                width="30%">
                {{ item.energy_display }}
              </md-table-cell>
              <md-table-cell
                md-label="Lieux concernés"
                width="30%">
                <ul class="energy-switch-places">
                  <li
                    v-for="(place, i) in item.places_display"
                    :key="`energy-switch-place-${i}`">{{ place }}</li>
                </ul>
              </md-table-cell>
              <md-table-cell>
                <md-button
                  @click="showUpdateEnergySwitchModal(item)"
                  class="md-primary">
                  Modifier
                </md-button>
              </md-table-cell>
              <md-table-cell>
                <md-button
                  @click="showConfirmUpdateModal(deleteEnergySwitch, item.id)"
                  class="md-primary">
                  Supprimer
                </md-button>
              </md-table-cell>
            </md-table-row>
          </md-table>
        </div>

        <div class="box box-form">
          <h3>Configurer les changements d'énergie, pour un ou plusieurs lieux :</h3>
          <div>
            <div class="md-layout md-gutter">
              <div class="md-layout-item">
                <md-field :class="{ 'md-has-value': true, 'md-invalid': scenarioEnergySwitchErrors.name }">
                  <label for="name">Nom</label>
                  <md-input
                    placeholder="Nom"
                    v-model="newEnergySwitch.name"
                    required />
                  <span
                    class="md-error"
                    v-if="scenarioEnergySwitchErrors.name">{{ scenarioEnergySwitchErrors.name }}</span>
                </md-field>
              </div>

              <div class="md-layout-item">
                <md-field :class="{ 'md-has-value': true, 'md-invalid': scenarioEnergySwitchErrors.energy }">
                  <label for="energy_switch_energy">Énergie</label>
                  <md-select
                    md-dense
                    md-class="md-menu-content-very-huge"
                    v-model="newEnergySwitch.energy"
                    name="energy_switch_energy"
                    id="id_energy_switch_energy"
                    placeholder="Choisissez la nouvelle énergie"
                    required>
                    <md-option
                      v-for="energy in energyList"
                      :key="energy.slug"
                      :value="energy.id">
                      {{ energy.label }}
                    </md-option>
                  </md-select>
                  <span
                    class="md-error"
                    v-if="scenarioEnergySwitchErrors.energy">{{ scenarioEnergySwitchErrors.energy }}</span>
                </md-field>
              </div>
              <div class="md-layout-item">
                <places-input
                  v-model="newEnergySwitch.selectedPlaces"
                  :required="true"
                  :places-type="energySwitchPLaceType"
                  :error-message="scenarioEnergySwitchErrors.selectedPlaces"
                  label="Lieux"
                />
              </div>
            </div>

            <div class="md-layout md-gutter">
              <div class="md-layout-item">
                <md-button
                  @click="showConfirmUpdateModal(createEnergySwitch, newEnergySwitch)"
                  class="md-raised md-primary float-right">
                  Ajouter le changement d'énergie
                </md-button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-40">
        <h2>Remarques / commentaires</h2>
        <div class="box">
          <md-field
            :class="{ 'md-has-value': true }">
            <label for="action_function">Notes</label>
            <md-textarea v-model="scenarioData.comments"/>
          </md-field>
          <div class="text-right">
            <md-button
              @click="saveScenarioComments"
              class="md-raised md-primary">
              Enregistrer
            </md-button>
          </div>
      </div></div>

      <div class="clearfix mt-40">
        <div class="float-right">
          <md-button
            @click="showConfirmUpdateModal(generateScenario, null)"
            class="md-raised md-primary">
            Calculer
          </md-button>
        </div>
      </div>

      <div>
        <md-dialog-confirm
          class="confirm-modal"
          :md-active.sync="confirmUpdateModal.showModal"
          md-title="Confirmer les modifications ?"
          md-content="<strong>Attention</strong>, cette action va écraser les résultats actuels et annuler les modifications saisies dans <strong>&#34;Affinage&#34;</strong>"
          md-confirm-text="Continuer"
          md-cancel-text="Annuler"
          @md-cancel="confirmUpdateModal.showModal=false"
          @md-confirm="confirmUpdateModal.callback(confirmUpdateModal.callbackArgs)" />

        <md-dialog
          class='update-modal'
          :md-active="updateScenarioAction.showModal && !confirmUpdateModal.showModal">
          <md-dialog-title>
            Modifier l'action "<em v-if='updateScenarioAction.scenarioAction'>{{ updateScenarioAction.scenarioAction.action_label }}</em>"
          </md-dialog-title>
          <md-dialog-content>
            <div class="md-layout md-gutter">
              <div class="md-layout-item">
                <md-field
                  :class="{ 'md-has-value': true, 'md-invalid': updateScenarioActionFieldsErrors.name }">
                  <label for="name">Nom</label>
                  <md-input
                    placeholder="Nom"
                    v-model="updateScenarioAction.name"
                    required />
                  <span
                    class="md-error"
                    v-if="updateScenarioActionFieldsErrors.name">{{ updateScenarioActionFieldsErrors.name }}</span>
                </md-field>
              </div>
              <!-- https://github.com/vuematerial/vue-material/issues/1681 -->
              <div class="md-layout-item">
                <md-field
                  :class="{ 'md-has-value': true, 'md-invalid': updateScenarioActionFieldsErrors.selectedAction }">
                  <label for="action">Action</label>
                  <md-select
                    md-dense
                    md-class="md-menu-content-very-huge"
                    v-model="updateScenarioAction.selectedAction"
                    name="action"
                    id="action"
                    disabled>
                    <md-option
                      v-for="action in actionList"
                      :key="action.id"
                      :value="action.id">
                      {{ action.label }}
                    </md-option>
                  </md-select>
                  <span
                    class="md-error"
                    v-if="updateScenarioActionFieldsErrors.selectedAction">{{ updateScenarioActionFieldsErrors.selectedAction }}</span>
                </md-field>
              </div>
              <div
                class="md-layout-item"
                v-if='updateScenarioAction.userActionType === "global-action"'>
                <md-field
                  :class="{ 'md-has-value': true, 'md-invalid': updateScenarioActionFieldsErrors.selectedFunction }">
                  <label for="action_function">Fonction</label>
                  <md-select
                    md-dense
                    v-model="updateScenarioAction.selectedFunction"
                    name="function"
                    id="function"
                    placeholder="Choisissez une fonction">
                    <md-option
                      v-for="func in functionList"
                      :key="func.slug"
                      :value="func.id">
                      {{ func.label }}
                    </md-option>
                  </md-select>
                  <span
                    class="md-error"
                    v-if="updateScenarioActionFieldsErrors.selectedFunction">{{ updateScenarioActionFieldsErrors.selectedFunction }}</span>
                </md-field>
              </div>
              <div
                class="md-layout-item"
                v-if='updateScenarioAction.userActionType === "global-action"'>
                <md-field
                  :class="{ 'md-has-value': true, 'md-invalid': updateScenarioActionFieldsErrors.selectedPlaceType }">
                  <label for="place_type">Type de lieux</label>
                  <md-select
                    md-dense
                    v-model="updateScenarioAction.selectedPlaceType"
                    name="place_type"
                    id="place_type"
                    placeholder="Choisissez un type de lieu">
                    <md-option
                      v-for="(placeType, i) in updatePlaceTypeList"
                      :key="i"
                      :value="placeType.value">
                      {{ placeType.label }}
                    </md-option>
                  </md-select>
                  <span
                    class="md-error"
                    v-if="updateScenarioActionFieldsErrors.selectedPlaceType">{{ updateScenarioActionFieldsErrors.selectedPlaceType }}</span>
                </md-field>
              </div>
            </div>
            <div class="md-layout md-gutter">
              <div
                class="md-layout-item"
                v-if='updateScenarioAction.userActionType === "place-action"'>
                <places-input
                  v-model="updateScenarioAction.selectedPlaces"
                  :required="true"
                  :places-type="updateActionplacesType"
                  label="Lieux concernés"
                />

              </div>
            </div>
            <div class="md-layout md-gutter">
              <div class="md-layout-item md-size-25">
                <md-field
                  :class="{ 'md-has-value': true, 'md-invalid': updateScenarioActionFieldsErrors.selectedYear }">
                  <label for="scenarioYear">Départ</label>
                  <md-select
                    v-model="updateScenarioAction.selectedYear"
                    name="year"
                    placeholder="Choisissez une année de départ"
                    required>
                    <md-option
                      v-for="scenarioYear in scenarioYears"
                      :key='scenarioYear'
                      :value="scenarioYear">{{ scenarioYear }}</md-option>
                  </md-select>
                  <span
                    class="md-error"
                    v-if="updateScenarioActionFieldsErrors.selectedYear">{{ updateScenarioActionFieldsErrors.selectedYear }}</span>
                </md-field>
              </div>
              <div class="md-layout-item flex__less">
                <YearlyValuesField
                  unit=" action(s)"
                  field-title="Rythme annuel"
                  counter-title="Nombre total"
                  :show-counter="true"
                  :years-cluser-list="scenarioYearsCluserList"
                  :initial-values-list="updateScenarioAction.rhythms"
                  :min-year="scenarioYear"
                  :max-year="scenarioMaxYear"
                  @validate:valuesList="updateScenarioAction.rhythms = $event" />
              </div>
            </div>
            <div class="md-layout md-gutter">
              <div class="md-layout-item">
                <div class="md-caption">Appliquer si</div>
                <div>
                  <md-checkbox
                    class="md-primary"
                    v-model="updateScenarioAction.ownerApplying">
                    Propriétaire occupant
                  </md-checkbox>
                  <md-checkbox
                    class="md-primary"
                    v-model="updateScenarioAction.coOwnerApplying">
                    Propriétaire bailleur
                  </md-checkbox>
                  <md-checkbox
                    class="md-primary"
                    v-model="updateScenarioAction.tenantApplying">
                    Locataire
                  </md-checkbox>
                  <md-checkbox
                    class="md-primary"
                    v-model="updateScenarioAction.partOwnerApplying">
                    Copropriétaire
                  </md-checkbox>
                </div>
              </div>
            </div>
          </md-dialog-content>

          <md-dialog-actions>
            <md-button
              class="md-primary"
              @click="updateScenarioAction.showModal = false">Annuler</md-button>
            <md-button
              class="md-primary"
              @click="showConfirmUpdateModal(updateIncludedAction, updateScenarioAction)">Modifier</md-button>
          </md-dialog-actions>
        </md-dialog>

        <md-dialog
          class='update-modal'
          :md-active="updateEnergySwitch.showModal && !confirmUpdateModal.showModal">
          <md-dialog-title>
            Modifier le changement d'énergie "<em v-if='updateEnergySwitch.energySwitch'>{{ updateEnergySwitch.energySwitch.name }}</em>"
          </md-dialog-title>
          <md-dialog-content>
            <div class="md-layout md-gutter">
              <div class="md-layout-item">
                <md-field
                  :class="{ 'md-has-value': true, 'md-invalid': updateScenarioEnergySwitchErrors.name }">
                  <label for="name">Nom</label>
                  <md-input
                    placeholder="Nom"
                    v-model="updateEnergySwitch.name"
                    required />
                  <span
                    class="md-error"
                    v-if="updateScenarioEnergySwitchErrors.name">{{ updateScenarioEnergySwitchErrors.name }}</span>
                </md-field>
              </div>

              <div class="md-layout-item">
                <md-field :class="{ 'md-has-value': true, 'md-invalid': updateScenarioEnergySwitchErrors.energy }">
                  <label for="energy_switch_energy">Énergie</label>
                  <md-select
                    md-dense
                    md-class="md-menu-content-very-huge"
                    v-model="updateEnergySwitch.energy"
                    name="energy_switch_energy"
                    id="id_update_energy_switch_energy"
                    placeholder="Choisissez la nouvelle énergie"
                    required>
                    <md-option
                      v-for="energy in energyList"
                      :key="energy.slug"
                      :value="energy.id">
                      {{ energy.label }}
                    </md-option>
                  </md-select>
                  <span
                    class="md-error"
                    v-if="updateScenarioEnergySwitchErrors.energy">{{ updateScenarioEnergySwitchErrors.energy }}</span>
                </md-field>
              </div>

              <div class="md-layout-item">
                <places-input
                  v-model="updateEnergySwitch.selectedPlaces"
                  :places-type="energySwitchPLaceType"
                  :required="true"
                  :error-message="updateScenarioEnergySwitchErrors.selectedPlaces"
                  label="Lieux"
                />
              </div>
          </div></md-dialog-content>

          <md-dialog-actions>
            <md-button
              class="md-primary"
              @click="updateEnergySwitch.showModal = false">Annuler</md-button>
            <md-button
              class="md-primary"
              @click="showConfirmUpdateModal(updateExistingEnergySwitch, updateEnergySwitch)">Modifier</md-button>
          </md-dialog-actions>
        </md-dialog>

        <md-dialog-alert
          :md-active.sync="simulationError.show"
          :md-title="simulationError.title"
          :md-content="simulationError.message"
          md-confirm-text="Fermer" />
      </div>
    </md-content>
  </div>
</template>

<script>
import session from '@/api/session'
import Multiselect from 'vue-multiselect'
import ScenarioHeader from '@/components/ScenarioHeader.vue'
import YearlyValuesField from '@/components/YearlyValuesField.vue'
import UserPlacesInputField from '@/components/UserPlacesInputField.vue'
import ValuesDisplayMixin from '@/mixins/ValuesDisplayMixin'
import {orderBy} from 'lodash'

export default {
  name: 'ScenarioActions',
  mixins: [ValuesDisplayMixin],
  components: {
    ScenarioHeader,
    YearlyValuesField,
    Multiselect,
    'places-input': UserPlacesInputField
  },
  data () {
    return {
      actionsCategories: [],
      actionsPlaceType: {},
      placeTypeValues: {'site': 1, 'building': 2, 'area': 3},
      functionList: [],
      energyList: [],
      scenarioData: {scenario_state: 'new'},
      scenarioYear: new Date().getFullYear(),
      scenarioMaxYear: 2050,
      scenarioYears: [],
      scenarioYearsCluserList: [],
      scenarioActions: [],
      placeExcludedActions: [],
      energySwitchPlaces: [],
      excludedActionsCurrentSort: 'place_details.identifier',
      excludedActionsCurrentSortOrder: 'asc',
      userPlaces: [],
      userActionType: 'global-action',

      newScenarioAction: {
        selectedActionCategory: null,
        selectedAction: null,
        selectedFunction: null,
        selectedPlaceType: null,
        selectedPlaces: [],
        selectedYear: null,
        rhythms: [],
        ownerApplying: true,
        coOwnerApplying: true,
        tenantApplying: true,
        partOwnerApplying: true
      },

      updateScenarioAction: {
        name: '',
        showModal: false,
        userActionType: null,
        scenarioAction: null,
        selectedAction: null,
        selectedFunction: null,
        selectedPlaceType: null,
        selectedPlaces: [],
        selectedYear: null,
        rhythms: [],
        ownerApplying: true,
        coOwnerApplying: true,
        tenantApplying: true,
        partOwnerApplying: true
      },

      newPlaceExcludedAction: {
        selectedActionCategory: null,
        selectedAction: null,
        selectedPlace: null,
        comment: null
      },

      energySwitchPLaceType: 'area',
      newEnergySwitch: {
        name: '',
        energy: null,
        selectedPlaces: []
      },
      updateEnergySwitch: {
        showModal: false,
        energySwitch: null,
        name: '',
        energy: null,
        selectedPlaces: []
      },

      confirmUpdateModal: {
        showModal: false,
        callback: null,
        callbackArgs: null
      },

      simulationError: {
        message: '',
        show: false,
        title: ''
      },

      scenarioActionFieldsErrors: {},
      updateScenarioActionFieldsErrors: {},
      placeExcludedActionFieldsErrors: {},
      scenarioEnergySwitchErrors: {},
      updateScenarioEnergySwitchErrors: {}
    }
  },
  mounted () {
    this.$store.dispatch('routing/update_title', 'Script')
    this.$store.dispatch('routing/update_return_kwargs', null)
    this.loadApi()
  },
  watch: {
    userActionType (val, oldVal) {
      if (oldVal !== val) {
        this.resetFormFields()
      }
    },
    'newScenarioAction.selectedAction': function (newValue, oldValue) {
      if (newValue !== oldValue) {
        delete this.scenarioActionFieldsErrors['selectedAction']
        this.newScenarioAction.selectedPlaces = []
      }
    },
    'updateScenarioAction.selectedAction': function (newValue, oldValue) {
      if (newValue !== oldValue) {
        delete this.updateScenarioActionFieldsErrors['selectedAction']
      }
    },
    'newPlaceExcludedAction.selectedAction': function (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.newPlaceExcludedAction.selectedPlace = []
      }
    },
    actionList (val) {
      const selectedActionCategory = this.newScenarioAction.selectedActionCategory || this.newPlaceExcludedAction.selectedActionCategory

      let selectedAction = this.newScenarioAction.selectedAction || this.newPlaceExcludedAction.selectedAction

      if (selectedAction !== null) {
        let actionInList = val.findIndex(function (a) { return a.id === selectedAction }) !== -1

        if (actionInList === false) {
          this.newScenarioAction.selectedAction = null
          this.newPlaceExcludedAction.selectedAction = null
          selectedAction = null
        }
      }

      if (selectedActionCategory !== null && selectedAction === null && val.length === 1) {
        this.newScenarioAction.selectedAction = val[0].id
        this.newPlaceExcludedAction.selectedAction = val[0].id
      }
    },
    placeList () {
      this.newScenarioAction.selectedPlaces = []
      this.newPlaceExcludedAction.selectedPlace = []
    },
    placeTypeList (placeTypeList) {
      if (this.newScenarioAction.selectedPlaceType in placeTypeList === false) {
        this.newScenarioAction.selectedPlaceType = null
      }
    }
  },
  computed: {
    actionList () {
      const actionsList = []
      const selectedActionCategory = this.newScenarioAction.selectedActionCategory || this.newPlaceExcludedAction.selectedActionCategory

      for (let i in this.actionsCategories) {
        let subCategories = this.actionsCategories[i].sub_categories
        for (let j in subCategories) {
          let actions = subCategories[j].actions
          for (let k in actions) {
            if (selectedActionCategory === null || selectedActionCategory === subCategories[j].id) {
              actionsList.push(actions[k])
            }
          }
        }
      }
      return actionsList
    },
    newActionplacesType () {
      const selectedAction = this.newScenarioAction.selectedAction
      if (selectedAction) {
        return this.actionsPlaceType[selectedAction]
      } else {
        return null
      }
    },
    updateActionplacesType () {
      const selectedAction = this.updateScenarioAction.selectedAction
      if (selectedAction) {
        return this.actionsPlaceType[selectedAction]
      } else {
        return null
      }
    },
    placeList () {
      const placeList = []
      const selectedAction = this.newPlaceExcludedAction.selectedAction

      this.userPlaces.forEach(userPlace => {
        if (selectedAction === null || this.actionsPlaceType[selectedAction] === userPlace.type) {
          placeList.push(userPlace)
        }
      })

      return placeList
    },
    placeTypeList () {
      const placeTypeList = [{value: '', label: ''}]
      const allPlaceTypes = [
        {value: 1, label: 'Site'},
        {value: 2, label: 'Bâtiment'},
        {value: 3, label: 'Zone'}
      ]
      const selectedAction = this.newScenarioAction.selectedAction
      allPlaceTypes.forEach(placeType => {
        if (!selectedAction || placeType.value <= this.placeTypeValues[this.actionsPlaceType[selectedAction]]) {
          placeTypeList.push(placeType)
        }
      })
      return placeTypeList
    },
    updatePlaceTypeList () {
      const placeTypeList = [{value: '', label: ''}]
      const allPlaceTypes = [
        {value: 1, label: 'Site'},
        {value: 2, label: 'Bâtiment'},
        {value: 3, label: 'Zone'}
      ]
      const selectedAction = this.updateScenarioAction.selectedAction

      allPlaceTypes.forEach(placeType => {
        if (!selectedAction || placeType.value <= this.placeTypeValues[this.actionsPlaceType[selectedAction]]) {
          placeTypeList.push(placeType)
        }
      })

      return placeTypeList
    },
    scenarioResultsValids () {
      return ['refine', 'results'].includes(this.scenarioData.scenario_state)
    }
  },
  methods: {
    showConfirmUpdateModal (callback, callbackArgs) {
      if (this.scenarioResultsValids) {
        this.confirmUpdateModal.callback = callback
        this.confirmUpdateModal.callbackArgs = callbackArgs
        this.confirmUpdateModal.showModal = true
      } else {
        callback(callbackArgs)
      }
    },
    showUpdateIncludedActionModal (scenarioAction) {
      this.updateScenarioAction.showModal = true
      this.updateScenarioAction.name = scenarioAction.name
      this.updateScenarioAction.scenarioAction = scenarioAction

      if (scenarioAction.places.length === 0) {
        this.updateScenarioAction.userActionType = 'global-action'
      } else {
        this.updateScenarioAction.userActionType = 'place-action'
      }

      this.updateScenarioAction.selectedAction = scenarioAction.action
      this.updateScenarioAction.selectedFunction = scenarioAction.function
      this.updateScenarioAction.selectedPlaceType = scenarioAction.place_type
      this.updateScenarioAction.selectedPlaces = []

      scenarioAction.places.forEach((placeId) => {
        const place = this.userPlaces.filter(place => place.id === placeId)
        if (place.length > 0) {
          this.updateScenarioAction.selectedPlaces.push(place[0])
        }
      })

      this.updateScenarioAction.selectedYear = scenarioAction.start_year
      this.updateScenarioAction.rhythms = scenarioAction.rhythms
      this.updateScenarioAction.ownerApplying = scenarioAction.owner_applying
      this.updateScenarioAction.coOwnerApplying = scenarioAction.co_owner_applying
      this.updateScenarioAction.tenantApplying = scenarioAction.tenant_applying
      this.updateScenarioAction.partOwnerApplying = scenarioAction.part_owner_applying
    },
    showUpdateEnergySwitchModal (energySwitch) {
      this.updateEnergySwitch.showModal = true
      this.updateEnergySwitch.energySwitch = energySwitch
      this.updateEnergySwitch.name = energySwitch.name
      this.updateEnergySwitch.energy = energySwitch.energy
      this.updateEnergySwitch.selectedPlaces = []

      energySwitch.places.forEach((placeId) => {
        const place = this.userPlaces.filter(place => place.id === placeId)
        if (place.length > 0) {
          this.updateEnergySwitch.selectedPlaces.push(place[0])
        }
      })
    },
    resetFormFields: function () {
      this.newScenarioAction.selectedActionCategory = null
      this.newScenarioAction.selectedAction = null
      this.newScenarioAction.selectedFunction = null
      this.newScenarioAction.selectedPlaceType = null
      this.newScenarioAction.selectedPlaces = []
      this.newScenarioAction.selectedYear = null
      this.newScenarioAction.ownerApplying = true
      this.newScenarioAction.coOwnerApplying = true
      this.newScenarioAction.tenantApplying = true
      this.newScenarioAction.partOwnerApplying = true
      this.newScenarioAction.rhythms = []
      this.newScenarioAction.name = ''
      for (let year = this.scenarioYear; year <= this.scenarioMaxYear; year++) {
        this.newScenarioAction.rhythms.push({ 'year': year, 'value': 1 })
      }

      this.newPlaceExcludedAction.selectedActionCategory = null
      this.newPlaceExcludedAction.selectedAction = null
      this.newPlaceExcludedAction.selectedPlace = null
      this.newPlaceExcludedAction.comment = null
      this.newPlaceExcludedAction.name = ''

      this.newEnergySwitch.name = ''
      this.newEnergySwitch.energy = null
      this.newEnergySwitch.selectedPlaces = []
    },
    async loadApi () {
      this.$store.dispatch('loading/begin_loading')

      const scenarioId = this.$route.params.scenarioId
      const scenarioDataRequest = session.get(`/api/scenario/${scenarioId}/`)
      const actionsCategoriesRequest = session.get('/api/actions/categories/')
      const functionsRequest = session.get('/api/parameters/functions/')
      const placesRequest = session.get('/api/userdata/places/', {params: {is_processed: true}})
      const scenarioActionRequest = session.get(`/api/scenario/${scenarioId}/scenario-action/`)
      const scenarioPlaceExcludedActionRequest = session.get(`/api/scenario/${scenarioId}/excluded-actions/`)
      const energySwitchPlacesRequest = session.get(`/api/scenario/${scenarioId}/energy-switch-places/`)
      const energyRequest = session.get('/api/parameters/energies/', {params: {showHeatPump: true}})

      const [
        scenarioDataResponse,
        actionsCategoriesResponse,
        functionsResponse,
        placesResponse,
        scenarioActionResponse,
        scenarioPlaceExcludedActionResponse,
        energySwitchPlacesResponse,
        energyResponse

      ] = await Promise.all([
        scenarioDataRequest,
        actionsCategoriesRequest,
        functionsRequest,
        placesRequest,
        scenarioActionRequest,
        scenarioPlaceExcludedActionRequest,
        energySwitchPlacesRequest,
        energyRequest
      ])

      this.scenarioData = scenarioDataResponse.data

      if (this.scenarioData.generating) {
        this.$router.push({name: 'Scenarios list'})
      }

      this.energyList = energyResponse.data
      this.actionsCategories = actionsCategoriesResponse.data
      this.functionList = functionsResponse.data
      this.userPlaces = placesResponse.data
      this.scenarioActions = scenarioActionResponse.data
      this.energySwitchPlaces = energySwitchPlacesResponse.data
      this.placeExcludedActions = this.excludedActionsCustomSort(scenarioPlaceExcludedActionResponse.data)

      this.$store.dispatch('routing/update_title', this.scenarioData.label)

      this.scenarioActions = []
      for (let i in scenarioActionResponse.data) {
        let scenarioAction = scenarioActionResponse.data[i]
        scenarioAction['update'] = false
        this.scenarioActions.push(scenarioAction)
      }

      this.functionList.unshift({'id': '', 'label': '', 'slug': ''})

      this.actionsPlaceType = {}
      for (let i in this.actionsCategories) {
        let subCategories = this.actionsCategories[i].sub_categories
        for (let j in subCategories) {
          const actions = subCategories[j].actions
          for (let k in actions) {
            this.actionsPlaceType[actions[k].id] = actions[k].place_type
          }
        }
      }

      // Init scenario year
      this.scenarioYear = this.scenarioData.year
      this.scenarioMaxYear = this.scenarioData.scenario_end_year
      let nbYears = this.scenarioMaxYear - this.scenarioYear + 1
      this.scenarioYears = Array.from(new Array(nbYears), (val, index) => index + this.scenarioYear)

      const nbPerYear = 10
      let nb = 0
      let yearCluster = []
      this.scenarioYearsCluserList = []

      for (let year = this.scenarioYear; year <= this.scenarioMaxYear; year++) {
        nb++
        if ((nb % nbPerYear) === 0) {
          yearCluster.push(year)
          this.scenarioYearsCluserList.push(yearCluster)
          yearCluster = []
        } else if ((nb % nbPerYear) === 1) {
          yearCluster.push(year)
        }
      }

      if (yearCluster.length > 0) {
        yearCluster.push(this.scenarioMaxYear)
        this.scenarioYearsCluserList.push(yearCluster)
      }

      this.resetFormFields()
      this.scenarioActionFieldsErrors = {}
      this.updateScenarioActionFieldsErrors = {}
      this.placeExcludedActionFieldsErrors = {}
      this.updateScenarioEnergySwitchErrors = {}

      if (this.scenarioData.generation_failed === true) {
        this.simulationError.message = this.scenarioData.generation_failed_message
        this.simulationError.title = 'Simulation impossible'
        this.simulationError.show = true
      }

      this.$store.dispatch('loading/end_loading')
    },
    async saveScenarioComments () {
      const scenarioId = this.$route.params.scenarioId
      this.$store.dispatch('loading/begin_loading')

      const response = await session.patch(`/api/scenario/settings/${scenarioId}/`, { 'comments': this.scenarioData.comments })
      this.scenarioData = response.data
      this.$store.dispatch('loading/end_loading')
      this.$store.commit('messages/SHOW', {
        label: 'Succès',
        description: 'Notes enregistrées avec succès',
        duration: 3000
      })
    },
    createScenarioAction ({ selectedActionCategory, selectedAction, selectedFunction, selectedPlaceType, selectedPlaces, selectedYear, rhythms, ownerApplying, coOwnerApplying, tenantApplying, partOwnerApplying, name }) {
      const scenarioId = this.$route.params.scenarioId
      const scenarioActionData = { 'scenario': scenarioId, 'name': name }

      if (selectedAction !== null) {
        scenarioActionData.action = selectedAction
      }

      if (this.userActionType === 'global-action') {
        if (selectedFunction !== null) {
          scenarioActionData.function = selectedFunction
        }

        if (selectedPlaceType !== null) {
          scenarioActionData.place_type = selectedPlaceType
        }
      } else {
        if (selectedPlaces !== null) {
          scenarioActionData.places = []

          for (let i in selectedPlaces) {
            scenarioActionData.places.push(selectedPlaces[i].id)
          }
        }
      }

      if (selectedYear !== null) {
        scenarioActionData.start_year = selectedYear
      }

      if (rhythms !== null) {
        scenarioActionData.rhythms = []
        for (let year = this.scenarioYear; year <= this.scenarioMaxYear; year++) {
          const scenarioActionRhythm = this.newScenarioAction.rhythms[this.newScenarioAction.rhythms.findIndex((i) => { return i.year === year })]

          let rhythm = 0
          if (scenarioActionRhythm && scenarioActionRhythm.value) {
            rhythm = scenarioActionRhythm.value
          }

          scenarioActionData.rhythms.push({'year': year, 'quantity': rhythm})
        }
      }

      if (ownerApplying !== null) {
        scenarioActionData.owner_applying = ownerApplying
      }

      if (coOwnerApplying !== null) {
        scenarioActionData.co_owner_applying = coOwnerApplying
      }

      if (tenantApplying !== null) {
        scenarioActionData.tenant_applying = tenantApplying
      }

      if (partOwnerApplying !== null) {
        scenarioActionData.part_owner_applying = partOwnerApplying
      }

      this.$store.dispatch('loading/begin_loading')

      session.post(`/api/scenario/${scenarioId}/scenario-action/`, scenarioActionData)
        .then(() => {
          this.loadApi()
        })
        .catch((error) => {
          const response = error.response
          this.scenarioActionFieldsErrors = {}

          if (response.status === 400) {
            for (let field in response.data) {
              const errorMessage = response.data[field]
              let appFieldName = null
              switch (field) {
                case 'name':
                  appFieldName = 'name'
                  break
                case 'action':
                  appFieldName = 'selectedAction'
                  break
                case 'function':
                  appFieldName = 'selectedFunction'
                  break
                case 'place_type':
                  appFieldName = 'selectedPlaceType'
                  break
                case 'places':
                  appFieldName = 'selectedPlaces'
                  break
                case 'start_year':
                  appFieldName = 'selectedYear'
                  break
                case 'owner_applying':
                  appFieldName = 'ownerApplying'
                  break
                case 'co_owner_applying':
                  appFieldName = 'coOwnerApplying'
                  break
                case 'tenant_applying':
                  appFieldName = 'tenantApplying'
                  break
                case 'part_owner_applying':
                  appFieldName = 'partOwnerApplying'
                  break
              }

              if (appFieldName !== null) {
                this.scenarioActionFieldsErrors[appFieldName] = errorMessage[0]
              }
            }
          }
        })
        .then(() => {
          this.$store.dispatch('loading/end_loading')
        })
    },
    updateIncludedAction ({ scenarioAction, selectedAction, selectedFunction, selectedPlaceType, selectedPlaces, selectedYear, rhythms, ownerApplying, coOwnerApplying, tenantApplying, partOwnerApplying, name }) {
      const scenarioId = this.$route.params.scenarioId
      const scenarioActionData = { 'scenario': scenarioId, 'name': name }

      if (selectedAction !== null) {
        scenarioActionData.action = selectedAction
      }

      if (this.updateScenarioAction.userActionType === 'global-action') {
        if (selectedFunction !== null) {
          scenarioActionData.function = selectedFunction
        }

        if (selectedPlaceType !== null) {
          scenarioActionData.place_type = selectedPlaceType
        }
      } else {
        if (selectedPlaces !== null) {
          scenarioActionData.places = []

          for (let i in selectedPlaces) {
            scenarioActionData.places.push(selectedPlaces[i].id)
          }
        }
      }

      if (selectedYear !== null) {
        scenarioActionData.start_year = selectedYear
      }

      if (rhythms !== null) {
        scenarioActionData.rhythms = []

        for (let year = this.scenarioYear; year <= this.scenarioMaxYear; year++) {
          const scenarioActionRhythm = this.updateScenarioAction.rhythms[this.updateScenarioAction.rhythms.findIndex(function (i) { return i.year === year })]

          let rhythm = 0

          if (scenarioActionRhythm && scenarioActionRhythm.value) {
            rhythm = scenarioActionRhythm.value
          }
          scenarioActionData.rhythms.push({'year': year, 'quantity': rhythm})
        }
      }

      if (ownerApplying !== null) {
        scenarioActionData.owner_applying = ownerApplying
      }

      if (coOwnerApplying !== null) {
        scenarioActionData.co_owner_applying = coOwnerApplying
      }

      if (tenantApplying !== null) {
        scenarioActionData.tenant_applying = tenantApplying
      }

      if (partOwnerApplying !== null) {
        scenarioActionData.part_owner_applying = partOwnerApplying
      }

      this.$store.dispatch('loading/begin_loading')

      session.patch(`/api/scenario/${scenarioId}/scenario-action/${scenarioAction.id}/`, scenarioActionData)
        .then(() => {
          this.updateScenarioAction.showModal = false
          this.loadApi()
        })
        .catch((error) => {
          const response = error.response
          this.updateScenarioActionFieldsErrors = {}

          if (response.status === 500) {
            this.updateScenarioAction.showModal = false
          }

          if (response.status === 400) {
            for (let field in response.data) {
              const errorMessage = response.data[field]
              let appFieldName = null

              switch (field) {
                case 'name':
                  appFieldName = 'name'
                  break
                case 'action':
                  appFieldName = 'selectedAction'
                  break
                case 'function':
                  appFieldName = 'selectedFunction'
                  break
                case 'place_type':
                  appFieldName = 'selectedPlaceType'
                  break
                case 'places':
                  appFieldName = 'selectedPlaces'
                  break
                case 'start_year':
                  appFieldName = 'selectedYear'
                  break
                case 'owner_applying':
                  appFieldName = 'ownerApplying'
                  break
                case 'co_owner_applying':
                  appFieldName = 'coOwnerApplying'
                  break
                case 'tenant_applying':
                  appFieldName = 'tenantApplying'
                  break
                case 'part_owner_applying':
                  appFieldName = 'partOwnerApplying'
                  break
              }

              if (appFieldName !== null) {
                this.updateScenarioActionFieldsErrors[appFieldName] = errorMessage[0]
              }
            }
          }
        })
        .then(() => {
          this.$store.dispatch('loading/end_loading')
        })
    },
    createPlaceExcludedAction ({ selectedActionCategory, selectedAction, selectedPlace, comment, name }) {
      const scenarioId = this.$route.params.scenarioId
      const placeExcludedActionData = {'scenario': scenarioId, 'name': name}

      if (selectedAction !== null) {
        placeExcludedActionData.action = selectedAction
      }

      if (selectedPlace !== null) {
        placeExcludedActionData.place = selectedPlace.id
      }

      if (comment !== null) {
        placeExcludedActionData.comment = comment
      }

      this.$store.dispatch('loading/begin_loading')

      session.post(`/api/scenario/${scenarioId}/excluded-actions/`, placeExcludedActionData)
        .then(() => {
          this.loadApi()
        })
        .catch((error) => {
          const response = error.response
          this.placeExcludedActionFieldsErrors = {}

          if (response.status === 400) {
            for (let field in response.data) {
              const errorMessage = response.data[field]
              let appFieldName = null

              switch (field) {
                case 'name':
                  appFieldName = 'name'
                  break
                case 'place':
                  appFieldName = 'place'
                  break
                case 'action':
                  appFieldName = 'selectedAction'
                  break
              }

              if (appFieldName !== null) {
                this.placeExcludedActionFieldsErrors[appFieldName] = errorMessage[0]
              }
            }
          }
        })
        .then(() => {
          this.$store.dispatch('loading/end_loading')
        })
    },
    deleteScenarioAction (scenarioActionId) {
      const scenarioId = this.$route.params.scenarioId

      this.$store.dispatch('loading/begin_loading')
      session.delete(`/api/scenario/${scenarioId}/scenario-action/${scenarioActionId}/`)
        .then(() => {
          this.loadApi()
        })
        .catch(() => {
          this.$store.dispatch('loading/end_loading')
        })
    },
    deletePlaceExcludedAction (placeExcludedActionId) {
      const scenarioId = this.$route.params.scenarioId

      this.$store.dispatch('loading/begin_loading')
      session.delete(`/api/scenario/${scenarioId}/excluded-actions/${placeExcludedActionId}/`)
        .then(() => {
          this.loadApi()
        })
        .catch(() => {
          this.$store.dispatch('loading/end_loading')
        })
    },
    createEnergySwitch ({ name, energy, selectedPlaces }) {
      const scenarioId = this.$route.params.scenarioId
      const energySwitchData = { 'scenario': scenarioId, 'name': name, 'energy': energy, places: [] }

      selectedPlaces.forEach(place => energySwitchData.places.push(place.id))

      this.$store.dispatch('loading/begin_loading')

      session.post(`/api/scenario/${scenarioId}/energy-switch-places/`, energySwitchData)
        .then(() => {
          this.loadApi()
        })
        .catch((error) => {
          const response = error.response
          this.scenarioEnergySwitchErrors = {}

          if (response.status === 400) {
            for (let field in response.data) {
              const errorMessage = response.data[field]
              let appFieldName = null
              switch (field) {
                case 'name':
                  appFieldName = 'name'
                  break
                case 'energy':
                  appFieldName = 'energy'
                  break
                case 'places':
                  appFieldName = 'selectedPlaces'
                  break
              }

              if (appFieldName !== null) {
                this.scenarioEnergySwitchErrors[appFieldName] = errorMessage[0]
              }
            }
          }
        })
        .then(() => {
          this.$store.dispatch('loading/end_loading')
        })
    },
    updateExistingEnergySwitch ({ energySwitch, name, energy, selectedPlaces }) {
      const scenarioId = this.$route.params.scenarioId
      const energySwitchData = { 'scenario': scenarioId, 'name': name, 'energy': energy, places: [] }

      selectedPlaces.forEach(place => energySwitchData.places.push(place.id))

      this.$store.dispatch('loading/begin_loading')

      session.patch(`/api/scenario/${scenarioId}/energy-switch-places/${energySwitch.id}/`, energySwitchData)
        .then(() => {
          this.updateEnergySwitch.showModal = false
          this.loadApi()
        })
        .catch((error) => {
          const response = error.response
          this.updateScenarioEnergySwitchErrors = {}

          if (response.status === 500) {
            this.updateEnergySwitch.showModal = false
          }

          if (response.status === 400) {
            for (let field in response.data) {
              const errorMessage = response.data[field]
              let appFieldName = null

              switch (field) {
                case 'name':
                  appFieldName = 'name'
                  break
                case 'energy':
                  appFieldName = 'energy'
                  break
                case 'places':
                  appFieldName = 'selectedPlaces'
                  break
              }

              if (appFieldName !== null) {
                this.updateScenarioEnergySwitchErrors[appFieldName] = errorMessage[0]
              }
            }
          }
        })
        .then(() => {
          this.$store.dispatch('loading/end_loading')
        })
    },
    deleteEnergySwitch (energySwitchId) {
      const scenarioId = this.$route.params.scenarioId

      this.$store.dispatch('loading/begin_loading')
      session.delete(`/api/scenario/${scenarioId}/energy-switch-places/${energySwitchId}/`)
        .then(() => {
          this.loadApi()
        })
        .catch(() => {
          this.$store.dispatch('loading/end_loading')
        })
    },
    generateScenario () {
      const scenarioId = this.$route.params.scenarioId
      session.get(`/api/scenario/${scenarioId}/generate/`)
        .then((response) => {
          this.$router.push({name: 'Scenarios list'})
        })
        .catch((error) => {
          const response = error.response
          switch (response.status) {
            case 500:
              this.$store.commit('errors/SHOW', {})
              break
            case 400:
              this.simulationError.message = response.data[0]
              this.simulationError.title = 'Simulation impossible'
              this.simulationError.show = true
              break
          }
        })
    },
    excludedActionsCustomSort (value) {
      const sortBy = this.excludedActionsCurrentSort
      const otherSortBy = sortBy === 'action_label' ? 'place_details.identifier' : 'action_label'
      const sortOrder = this.excludedActionsCurrentSortOrder
      return orderBy(value, [sortBy, otherSortBy], [sortOrder, 'asc'])
    },
    showSimulationError () {
      this.simulationError.show = true
    }
  },
  filters: {
    ownerStatus (value) {
      let status = ''

      if (value.owner_applying) {
        status += 'PO'
      }

      if (value.co_owner_applying) {
        if (status.length > 0) {
          status += ' - '
        }
        status += 'PB'
      }

      if (value.tenant_applying) {
        if (status.length > 0) {
          status += ' - '
        }
        status += 'L'
      }

      if (value.part_owner_applying) {
        if (status.length > 0) {
          status += ' - '
        }
        status += 'C'
      }

      return status
    },
    toInteger (value) {
      return parseInt(value)
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../stylesheets/variables";

.mt-40{
  margin-top: 40px;
}

.scenario-update-modal {
  margin: 0;
}
.md-accent-link{
  color: white !important;
}
.script__wrapper {
  padding: 3rem;
  padding-top: 1rem;
  overflow: auto;
  height: calc(100vh - 64*2px);
}
.top-actions{
  margin-top: 1rem;
}
.box {
  padding: 20px;
  background-color: project-theme('background');
  zoom: 1;
}

.multiselect-transparent {
  >>> .multiselect__tags,
  >>> .multiselect__single,
  >>> .multiselect__input {
    background: transparent;
    color: #717171;
  }
}

.text-right{
  text-align: right;
}

.md-empty-state{
  max-width: 1200px;
  padding-top: 10px;
  padding-bottom: 20px;
}

.energy-switch-places{
  list-style:none;
  padding:0;
}

.box-form{
  padding-left: 40px;
  padding-right: 40px;
}

</style>



// WEBPACK FOOTER //
// src/views/ScenarioScript.vue